import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MLoader from '@/components/ui/atoms/MLoader';
import MDialog from '@/components/ui/atoms/MDialog';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { ListItem } from '@/components/ui/atoms/MListItem';
import { IconEdit, IconCircleX, IconPlus } from '@tabler/icons-react';
import styles from '@/pages/documents/document.module.css';
import MInfiniteScroll from '@/components/ui/atoms/MInfiniteScroll';
import { PAGE_SIZE } from '@/utils/constants';
import { MButton } from '@/components/ui/atoms/MButtons';
import IconBubble from '@/components/icons/IconBubble';
import FormHeader from '@/components/ui/organisms/MHeader';
import { getMemberInfo } from '@/hooks/useMembers';
import useHealthMemory from '@/hooks/useHealthMemory';
import MSpacer from '@/components/ui/atoms/MSpacer';
import MEmptyCard from '@/components/ui/organisms/MEmptyCard';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

// Placeholder types and hooks for demonstration
interface HealthMemory {
  id: string;
  title: string;
  year: number;
  details: string;
  createdAt?: string;
}

const HealthMemoryList: React.FC = () => {

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState<HealthMemory | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;

  const [page, setPage] = useState(2); // next page to fetch
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [memories, setMemories] = useState<HealthMemory[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const { healthMemories, loading, error, fetchHealthMemories, deleteHealthMemory } =
    useHealthMemory(memberId);

  useEffect(() => {
    if (!memberId) return;
    // (Re)load for selected member, always page 1
    (async () => {
      setIsLoading(true);
      setInitialLoadDone(false);

      setMemories([]);
      setPage(2);
      setHasMore(true);

      const data = await fetchHealthMemories({ page: 1, pageSize: PAGE_SIZE });

      setMemories(data || []);
      setPage(2);
      setHasMore((data || []).length === PAGE_SIZE);

      setIsLoading(false);
      setInitialLoadDone(true);
      
      // Track page load with total memories count
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
        additionalUserProperties: {
          total_memories: String((data || []).length),
        },
      });
    })();
  }, [memberId]);

  // Infinite scroll fetch
  const fetchNextPage = async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const data = await fetchHealthMemories({ page, pageSize: PAGE_SIZE });
    setMemories(prev => [...prev, ...(data || [])]);
    setPage(prev => prev + 1);
    setHasMore((data || []).length === PAGE_SIZE);
    setIsLoading(false);
  };

  if (loading) return <MLoader />;
  if (error)
    return (
      <MDialog
        isOpen={true}
        title="Could not load health memory notes"
        description={error}
        primaryCtaText="Retry"
        onPrimaryCta={() => window.location.reload()}
        onClose={() => window.location.reload()}
      />
    );

  return (
    <div className="profile-page-root bg-white">
      <FormHeader
        subHeading="Health memory"
        module="healthmemory"
        memberInfo={memberInfo}
        showProfilePickerRow
        showGradient
        onAdd={() => navigate('/documents/health-memory/add')}
        learnMoreContent={
          <>
            Some health experiences never make it into formal records--like an injury,
            a resolved condition, or a special need you or your loved ones recall.
            <br />
            <br />
            Capturing them as notes here adds missing context and builds a fuller
            health story to support better care.
          </>
        }
      />

      <MSpacer mt={32} />

      <div className="profile-flex-col-gap" style={{ marginTop: 0 }}>
        {!initialLoadDone || isLoading ? (   
          <MLoader />
        ) : memories.length === 0 ? (
          <MEmptyCard
            primaryTitle="No health memory notes yet"
            icon={<IconBubble />}
            primaryCTA={{
              text: 'Add health memory note ',
              onClick: () => navigate('/documents/health-memory/add'),
            }}
          />
        ) : (
          <MInfiniteScroll
            data={memories}
            hasMore={hasMore}
            isLoading={isLoading}
            onLoadMore={fetchNextPage}
            renderItem={(memory, idx) => (
              <div key={memory.id} style={{ position: 'relative' }}>
                <ListItem
                  usage="crud"
                  type="2-line"
                  variant="purple"
                  title={memory.title}
                  secondaryText={`In ${memory.year?.toString() || ''}`}
                  actions={[
                    {
                      id: 'edit',
                      label: 'Edit',
                      icon: <IconEdit size={20} />,
                      onClick: () =>
                        navigate(
                          `/documents/health-memory/edit?mode=edit&id=${memory.id}`
                        ),
                    },
                    {
                      id: 'delete',
                      label: 'Delete',
                      icon: <IconCircleX size={20} />,
                      onClick: () => {
                        setMemoryToDelete(memory);
                        setShowDeleteModal(true);
                        
                        // Track delete click
                        trackUserDataLoaded({
                          memberInfo,
                          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
                          screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
                          action: 'delete_click',
                        });
                      },
                      className: 'delete',
                    },
                  ]}
                />
              </div>
            )}
          />
        )}
      </div>

      {showDeleteModal && memoryToDelete && (
        <MDialog
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setMemoryToDelete(null);
          }}
          title="Delete this memory?"
          description="This memory will be permanently removed from your health records."
          primaryCtaText="Yes"
          onPrimaryCta={async () => {
            if (!memoryToDelete) return;
            await deleteHealthMemory(memoryToDelete.id);

            // Track delete confirmation
            trackUserDataLoaded({
              memberInfo,
              screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
              screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
              action: 'delete_confirm',
            });

            setShowDeleteModal(false);
            setMemoryToDelete(null);

            setIsLoading(true);
            const data = await fetchHealthMemories({
              page: 1,
              pageSize: PAGE_SIZE,
            });
            setMemories(data || []);
            setPage(2);
            setHasMore((data || []).length === PAGE_SIZE);
            setIsLoading(false);
          }}
          secondaryCtaText="No"
          onSecondaryCta={() => {
            setShowDeleteModal(false);
            setMemoryToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default HealthMemoryList;
